import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRamadan } from "@/hooks/useRamadan";
import { Moon, Eye, X, Compass, Heart, MessageCircle, User, Sparkles, Bell } from "lucide-react";
import { BrandEmblem } from "@/components/BrandEmblem";
import { api } from "@/lib/api";

// New brand logo — deep-forest heart + gold leaf accent (SVG, no external asset)
const BrandLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Find Your Naseeb">
    <path d="M24 42s-14-8.6-14-20a9 9 0 0 1 16-5.6A9 9 0 0 1 38 22c0 11.4-14 20-14 20Z" fill="#1F4A3F" />
    <path d="M27 8c-3 3-3 7-1 10 3-1 5-4 5-8 0-1-2-2-4-2Z" fill="#C9A24A" />
    <circle cx="24" cy="22" r="2" fill="#F6F0E4" />
  </svg>
);
void BrandLogo;

function NotificationsBell() {
  const [unread, setUnread] = React.useState(0);
  React.useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (mounted) setUnread(data.unread || 0);
      } catch {}
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  return (
    <Link to="/notifications" className="relative inline-flex items-center justify-center h-9 w-9 rounded-full text-brown-600 hover:bg-sand-100 focus:outline-none focus:ring-2 focus:ring-olive-500" data-testid="nav-bell" aria-label={`Notifikationer${unread ? ` (${unread} nye)` : ""}`}>
      <Bell className="h-5 w-5" />
      {unread > 0 && (
        <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center" data-testid="bell-badge">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRamadan = useRamadan();

  const authed = user && typeof user === "object";
  const isAdmin = authed && (user.role === "admin" || user.role === "moderator");
  const isWali = authed && user.role === "wali";
  const impersonated = authed && user.impersonated_by;
  const isAuthPage = ["/login", "/register", "/forgot-password", "/reset-password"].some((p) => location.pathname.startsWith(p));
  const showBottomNav = authed && !isAdmin && !isWali && !isAuthPage;

  const handleLogout = async () => { await logout(); navigate("/"); };
  const handleStopImpersonation = async () => {
    try { await stopImpersonation(); navigate("/admin"); }
    catch { await logout(); navigate("/login"); }
  };

  return (
    <div className={`min-h-screen flex flex-col bg-sand-50 ${showBottomNav ? "pb-20" : ""}`}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-olive-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-full focus:shadow-soft" data-testid="skip-to-content">
        Spring til indhold
      </a>
      {impersonated && (
        <div className="bg-gold-500 text-olive-900 text-sm border-b border-gold-600" data-testid="impersonation-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-medium">
              <Eye className="h-4 w-4" />
              <span>Du ser som <strong>{user.name || user.email}</strong> · admin: {user.impersonated_by.name || user.impersonated_by.email}</span>
            </div>
            <button onClick={handleStopImpersonation} className="inline-flex items-center gap-1 bg-olive-700 hover:bg-olive-600 text-white rounded-full px-3 py-1 text-xs" data-testid="stop-impersonation-btn">
              <X className="h-3 w-3" /> Stop og vend tilbage
            </button>
          </div>
        </div>
      )}
      {isRamadan && (
        <div className="bg-olive-700 text-gold-400 text-xs" data-testid="ramadan-banner">
          <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2">
            <Moon className="h-3.5 w-3.5" /> Ramadan Mubarak — måtte jeres intentioner blive velsignede
          </div>
        </div>
      )}
      <header className="bg-sand-50/95 backdrop-blur-sm sticky top-0 z-40 border-b border-sand-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <BrandEmblem size={38} animate />
            <div className="leading-none">
              <div className="font-serif text-lg text-olive-700 tracking-tight">Find Your Naseeb</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brown-500 mt-0.5">Modern rishta</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            {authed && isAdmin && (
              <NavLink to="/admin" className={({ isActive }) => `transition ${isActive ? "text-gold-600" : "text-gold-600/80 hover:text-gold-500"}`} data-testid="nav-admin">Admin</NavLink>
            )}
            {authed && isWali && (
              <NavLink to="/wali" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-wali-dashboard">Wali-dashboard</NavLink>
            )}
            {authed && !isAdmin && !isWali && (
              <>
                <NavLink to="/matches" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-matches">Discover</NavLink>
                <NavLink to="/interests" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-interests">Likes</NavLink>
                <NavLink to="/chat" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-chat">Messages</NavLink>
                <NavLink to="/family" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-family">Wali</NavLink>
                <NavLink to="/profile" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-profile">Profile</NavLink>
                <NavLink to="/pricing" className={({ isActive }) => `transition ${isActive ? "text-olive-600" : "text-brown-600 hover:text-olive-600"}`} data-testid="nav-pricing">Premium</NavLink>
              </>
            )}
            {!authed && (
              <>
                <NavLink to="/pricing" className="text-brown-600 hover:text-olive-600" data-testid="nav-pricing-public">Premium</NavLink>
                <a href="#how" className="text-brown-600 hover:text-olive-600">Sådan virker det</a>
                <a href="#values" className="text-brown-600 hover:text-olive-600">Vores værdier</a>
              </>
            )}
          </nav>
          <div className="flex items-center gap-3">
            {authed ? (
              <>
                {!isAdmin && !isWali && <NotificationsBell />}
                {isWali && <NotificationsBell />}
                <span className="hidden sm:inline text-sm text-brown-500 mr-2" data-testid="user-name">
                  {user.name}
                  {isAdmin && <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider bg-gold-500/90 text-white px-1.5 py-0.5 rounded-full" data-testid="admin-badge">Admin</span>}
                  {isWali && <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider bg-olive-500/90 text-white px-1.5 py-0.5 rounded-full" data-testid="wali-badge">Wali</span>}
                </span>
                <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="logout-btn">Log ud</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm" data-testid="header-login">Log ind</Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-4" data-testid="header-register">Opret</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-1" tabIndex={-1}>{children}</main>

      {showBottomNav && (
        <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
          <div className="mx-3 mb-3 rounded-3xl bg-white/95 backdrop-blur-xl border border-sand-300/70 shadow-[0_-8px_32px_-8px_rgba(47,93,80,0.15)]">
            <div className="grid grid-cols-5 h-16 px-1">
              <TabItem to="/matches" icon={<Compass className="h-5 w-5" strokeWidth={2.2} />} label="Discover" testid="tab-discover" />
              <TabItem to="/interests" icon={<Heart className="h-5 w-5" strokeWidth={2.2} />} label="Likes" testid="tab-likes" />
              <TabItem to="/matches?tab=my" icon={<Sparkles className="h-5 w-5" strokeWidth={2.2} />} label="Matches" testid="tab-matches" />
              <TabItem to="/chat" icon={<MessageCircle className="h-5 w-5" strokeWidth={2.2} />} label="Messages" testid="tab-messages" />
              <TabItem to="/profile" icon={<User className="h-5 w-5" strokeWidth={2.2} />} label="Profile" testid="tab-profile" />
            </div>
          </div>
        </nav>
      )}

      <footer className={`bg-sand-100 border-t border-sand-300 py-10 mt-16 ${showBottomNav ? "mb-16" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-3 text-sm">
          <div>
            <div className="flex items-center gap-2"><BrandEmblem size={28} /><span className="font-serif text-olive-700 text-lg">Find Your Naseeb</span></div>
            <p className="mt-3 text-brown-500 max-w-xs leading-relaxed">En seriøs, værdibaseret muslimsk ægteskabsplatform. Hver profil gennemgås. Hver samtale er privat.</p>
          </div>
          <div>
            <div className="eyebrow">Platform</div>
            <ul className="mt-3 space-y-1.5 text-brown-600">
              <li><Link to="/pricing" className="hover:text-olive-600">Medlemskab</Link></li>
              <li><Link to="/profile" className="hover:text-olive-600">Opret din profil</Link></li>
              <li><Link to="/login" className="hover:text-olive-600">Log ind</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow">Vores løfte</div>
            <ul className="mt-3 space-y-1.5 text-brown-600">
              <li>Manuelt gennemgåede profiler</li>
              <li>Privat, filtreret samtale</li>
              <li>Selv-hostet og portabel</li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-sand-300 text-center text-xs text-brown-500">© 2026 Find Your Naseeb — med respekt og intention.</div>
      </footer>
    </div>
  );
}

function TabItem({ to, icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/matches"}
      className={({ isActive }) =>
        `group relative flex flex-col items-center justify-center gap-1 rounded-2xl m-1 transition-all duration-300 ${
          isActive
            ? "bg-gradient-to-br from-olive-500 to-olive-600 text-white shadow-lg shadow-olive-500/30 scale-[1.02]"
            : "text-brown-500 hover:text-olive-600 hover:bg-sand-100/60 active:scale-95"
        }`
      }
      data-testid={testid}
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-1 w-6 rounded-full bg-gold-500 shadow-md shadow-gold-500/50" aria-hidden />}
          <span className={`transition-transform duration-300 ${isActive ? "-translate-y-0.5" : "group-hover:-translate-y-0.5"}`}>
            {icon}
          </span>
          <span className={`text-[9px] font-medium uppercase tracking-wider transition-opacity ${isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"}`}>{label}</span>
        </>
      )}
    </NavLink>
  );
}
